import React, {
  type ComponentType,
  useCallback,
  useMemo,
} from 'react';
import { type IAdsCopilotProps } from '@bingads-webui/ads-copilot-ui';
import { getDisplayName } from '@bingads-webui-react/hoc-utils';
import { LiveAgentConnectionState } from '../../helpers';
import { LiveAgentEndChat } from '../live-agent-end-chat';
import { CopilotLiveAgentBanner, LiveAgentBanner } from '../live-agent-banner';

// TODO: Uncomment this when all live agent dependency props are removed
// type PickIAdsCopilotProps = Pick<IAdsCopilotProps, 'botServiceConfig' | 'renderActivity' | 'renderTopComponent' | 'renderBottomComponent'>;

export interface WithLiveAgentProps extends IAdsCopilotProps {
  isLiveAgentOnlyMode?: boolean;
  shouldResumeConversation: { current: boolean };
  liveAgentState: LiveAgentConnectionState;
  isLiveAgentTyping: boolean;
  closeLiveAgentChat: (shouldResumeConversation: boolean) => void;
  onEndLiveAgentChatClick?: () => void;
}

export const withLiveAgent = <T extends WithLiveAgentProps>(WrappedComponent: ComponentType<T>): ComponentType<T> => {
  const HOC = (props: T) => {
    const {
      isLiveAgentOnlyMode,
      isCopilotDegraded,
      liveAgentState,
      isLiveAgentTyping,
      renderTopComponent,
      onEndLiveAgentChatClick,
      onClose,
      showViewPrompt,
    } = props;

    const handleRenderHeaderComponent = useCallback<NonNullable<IAdsCopilotProps['renderHeaderComponent']>>(() => {
      if (isLiveAgentOnlyMode) {
        return <LiveAgentBanner liveAgentState={liveAgentState} isLiveAgentTyping={isLiveAgentTyping} onClose={onClose} />;
      }
      return liveAgentState !== LiveAgentConnectionState.DISCONNECTED && <CopilotLiveAgentBanner liveAgentState={liveAgentState} onCancelClick={onEndLiveAgentChatClick!} isLiveAgentTyping={isLiveAgentTyping} />;
    }, [isLiveAgentTyping, liveAgentState, onClose, onEndLiveAgentChatClick]);

    const handleRenderFooterComponent = useCallback<NonNullable<IAdsCopilotProps['renderFooterComponent']>>(() => (
      liveAgentState !== LiveAgentConnectionState.DISCONNECTED && <LiveAgentEndChat liveAgentState={liveAgentState} onEndChatClick={onEndLiveAgentChatClick} />
    ), [liveAgentState, onEndLiveAgentChatClick]);

    const handleRenderTopComponent = useMemo(() => (!isLiveAgentOnlyMode ? renderTopComponent : undefined), [isLiveAgentOnlyMode, renderTopComponent]);

    const shouldShowViewPrompt = useMemo(() => {
      if (isCopilotDegraded || isLiveAgentOnlyMode) {
        return false;
      }
      return liveAgentState === LiveAgentConnectionState.DISCONNECTED;
    }, [isCopilotDegraded, isLiveAgentOnlyMode, liveAgentState]);

    const shouldDisableInput = useMemo(() => isLiveAgentOnlyMode && liveAgentState === LiveAgentConnectionState.DISCONNECTED, [isLiveAgentOnlyMode, liveAgentState]);

    return (
      <WrappedComponent
        {...props}
        showViewPrompt={showViewPrompt && shouldShowViewPrompt}
        renderHeaderComponent={handleRenderHeaderComponent}
        renderFooterComponent={handleRenderFooterComponent}
        showHeader={!isLiveAgentOnlyMode}
        renderTopComponent={handleRenderTopComponent}
        shouldDisableInput={shouldDisableInput}
      />
    );
  };

  HOC.displayName = `withLiveAgent(${getDisplayName(WrappedComponent)})`;

  return HOC;
};
